import React, { Suspense, useState, useEffect } from "react"
import { groupBy } from "lodash"
import { useContext } from "react"
import { LayoutContext } from "../Layout/Layout"
import { ThemeDivider } from "trimble-terra"
import { formatThemePreset } from "../../utils/organism-formatting/LayoutComponents/formatLayoutFunctions"

const FormatAndRenderHeroLead = React.lazy(() =>
  import("../../utils/organism-formatting/heroes/FormatAndRenderHeroLead")
)
const FormatAndRenderHeroLeadSearch = React.lazy(() =>
  import("../../utils/organism-formatting/heroes/FormatAndRenderHeroLeadSearch")
)
const FormatAndRenderSimpleHero = React.lazy(() =>
  import("../../utils/organism-formatting/heroes/FormatAndRenderSimpleHero")
)

const FormatAndRenderLayoutStatistics = React.lazy(() =>
  import(
    "../../utils/organism-formatting/LayoutComponents/FormatAndRenderLayoutStatistics"
  )
)
const FormatAndRenderLayoutFaq = React.lazy(() =>
  import(
    "../../utils/organism-formatting/LayoutComponents/FormatAndRenderLayoutFaq"
  )
)

const FormatAndRenderLayoutTestimonials = React.lazy(() =>
  import(
    "../../utils/organism-formatting/LayoutComponents/FormatAndRenderLayoutTestimonials"
  )
)

const FormatAndRenderLayoutLogoGrid = React.lazy(() =>
  import(
    "../../utils/organism-formatting/LayoutComponents/FormatAndRenderLayoutLogoGrid"
  )
)

const FormatAndRenderLayoutInlineMedia = React.lazy(() =>
  import(
    "../../utils/organism-formatting/LayoutComponents/FormatAndRenderLayoutInlineMedia"
  )
)

const FormatAndRenderLayoutItemizedList = React.lazy(() =>
  import(
    "../../utils/organism-formatting/LayoutComponents/FormatAndRenderLayoutItemizedList"
  )
)

const FormatAndRenderLayoutRelatedSubjects = React.lazy(() =>
  import(
    "../../utils/organism-formatting/LayoutComponents/FormatAndRenderLayoutRelatedSubjects"
  )
)

const FormatAndRenderLayoutCards = React.lazy(() =>
  import(
    "../../utils/organism-formatting/LayoutComponents/FormatAndRenderLayoutCards"
  )
)

const FormatAndRenderLayoutPricingCards = React.lazy(() =>
  import(
    "../../utils/organism-formatting/LayoutComponents/FormatAndRenderLayoutPricingCards"
  )
)

const FormatAndRenderLayoutGeneral = React.lazy(() =>
  import(
    "../../utils/organism-formatting/LayoutComponents/FormatAndRenderLayoutGeneral"
  )
)

const FormatAndRenderLayoutProductRecommendations = React.lazy(() =>
  import(
    "../../utils/organism-formatting/LayoutComponents/FormatAndRenderLayoutProductRecommendations"
  )
)

const FormatAndRenderLayoutFeatureCarousel = React.lazy(() =>
  import(
    "../../utils/organism-formatting/LayoutComponents/FormatAndRenderLayoutFeatureCarousel"
  )
)
const FormatAndRenderLayoutWorkflow = React.lazy(() =>
  import(
    "../../utils/organism-formatting/LayoutComponents/FormatAndRenderLayoutWorkflow"
  )
)
const FormatAndRenderLayoutSubjectSpotlight = React.lazy(() =>
  import(
    "../../utils/organism-formatting/LayoutComponents/FormatAndRenderLayoutSubjectSpotlight"
  )
)

const FormatAndRenderLayoutBlogDigest = React.lazy(() =>
  import(
    "../../utils/organism-formatting/LayoutComponents/FormatAndRenderLayoutBlogDigest"
  )
)

const FormatAndRenderLayoutForm = React.lazy(() =>
  import(
    "../../utils/organism-formatting/LayoutComponents/FormatAndRenderLayoutForm"
  )
)

const FormatAndRenderLayoutResourceGrid = React.lazy(() =>
  import(
    "../../utils/organism-formatting/LayoutComponents/FormatAndRenderLayoutResourceGrid"
  )
)

const FormatAndRenderLayoutComparisonTable = React.lazy(() =>
  import(
    "../../utils/organism-formatting/LayoutComponents/FormatAndRenderLayoutComparisonTable"
  )
)

function getThemeFromPreset(formattedTheme) {
  switch (formattedTheme) {
    case "t2-preset-theme-one":
      return { backgroundColor: "white", color: "#353A40" }
    case "t2-preset-theme-two":
      return { backgroundColor: "#F1F1F6", color: "rgb(37, 42, 46);" }
    case "t2-preset-theme-three":
      return { backgroundColor: "rgb(37, 42, 46)", color: "white" }
    case "t2-preset-theme-four":
      return { backgroundColor: "rgb(0, 99, 163)", color: "white" }
    case "t2-preset-theme-five":
      return { backgroundColor: "rgb(0, 48, 84)", color: "white" }
    case "t2-preset-theme-six":
      return { backgroundColor: "transparent", color: "rgb(37, 42, 46)" }
    case "t2-preset-theme-seven":
      return { backgroundColor: "#1b2023", color: "white" }
    default:
      return { backgroundColor: "white", color: "black" }
  }
}

const PageSections = ({
  pageSections,
  location,
  locale = "en",
  overrideTheme,
  nested,
  proseText,
  marginTopAndBottom,
  appData,
  ignoreFocalPoint,
  productPage,
  region,
  videoProgress,
  setVideoProgress,
  isNested,
  tab,
  siteAppData,
  currentSpaceId,
  parentIndex,
  products,
  siteIdentifier,
  isPageSection,
}) => {
  const [isSmallScreen, setIsSmallScreen] = useState(false)
  const { homepageSlug } = useContext(LayoutContext)

  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 600)
    }
    handleResize() // Call handler right away so state gets updated with initial window size
    window.addEventListener("resize", handleResize)

    // Cleanup the event listener on component unmount
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  let prevTheme = null // Track previous theme

  const allPageSections = pageSections?.map((content, i) => {
    return { type: content?.__typename, index: i, id: content?.contentfulId }
  })

  const groupedPageSections = groupBy(allPageSections, "type")

  const findSectionNumber = (type, id) => {
    const section = groupedPageSections[type]
    return section?.findIndex(item => item?.id === id) + 1 || 1
  }

  return pageSections?.map((content, i) => {
    // Get the next content to determine if a spacer is needed
    const nextContent =
      content?.__typename === "ContentfulLayoutFeatureCarousel"
        ? pageSections[i + 1]
        : null

    const nextFormattedTheme =
      formatThemePreset(nextContent?.theme) || overrideTheme

    const nextTheme = getThemeFromPreset(nextFormattedTheme)

    const currentTheme =
      (!content?.__typename?.toLowerCase()?.includes("hero") &&
        content?.theme) ||
      overrideTheme
    const component = renderPageSections({
      i: parentIndex || i,
      content,
      location,
      locale,
      overrideTheme,
      nested,
      proseText,
      marginTopAndBottom,
      appData,
      ignoreFocalPoint,
      productPage,
      region,
      videoProgress,
      setVideoProgress,
      isNested,
      tab,
      siteAppData,
      homepageSlug,
      currentSpaceId,
      products,
      siteIdentifier,
      isPageSection,
    })

    const formattedTheme = formatThemePreset(content?.theme)

    let theme = getThemeFromPreset(formattedTheme)

    const isSimpleHeroType =
      content?.__typename?.toLowerCase() === "contentfulherosimple"
    const isWhiteTheme = content?.theme === "White"

    const themeDivider =
      (prevTheme && prevTheme === currentTheme && isSmallScreen) ? (
        <div className={`${formattedTheme}`}>
          <ThemeDivider theme={theme} currentTheme={formattedTheme} />
        </div>
      ) : null

    prevTheme = currentTheme // Update the previous theme

    //
    const style =
      content?.__typename === "ContentfulLayoutFeatureCarousel"
        ? {
            backgroundColor: nextTheme?.backgroundColor || "white",
          }
        : {}

    // Only add spacer if the next content follows a Feature Carousel and has a type name.
    const shouldAddSpacer = nextContent?.__typename
    const spacer = shouldAddSpacer ? (
      <div
        className="spacer"
        style={{ backgroundColor: nextTheme?.backgroundColor || "white" }}
      />
    ) : null

    const componentAnchorName = type => {
      const anchorName = type
        ?.split(/(?<![A-Z])(?=[A-Z])/)
        ?.filter(item => {
          return item !== "Contentful"
        })
        ?.filter(item => {
          return item !== "Layout"
        })
        ?.join("-")
        ?.toLowerCase()
      return anchorName
    }

    if (!nested) {
      return (
        <div
          key={`section-wrapper-${i}`}
          id={`${componentAnchorName(content?.__typename)}-${findSectionNumber(
            content?.__typename,
            content?.contentfulId
          )}`}
          className={content?.theme ? formattedTheme : ""}
        >
          {!isSimpleHeroType && themeDivider}
          {component}
          {isSimpleHeroType && themeDivider}
          {/* {spacer} */}
        </div>
      )
    } else {
      return (
        <React.Fragment key={`section-wrapper-${i}`}>
          {themeDivider}
          {component}
          {/* {spacer} */}
        </React.Fragment>
      )
    }
  })
}

export default PageSections

const renderPageSections = ({
  i,
  content,
  location,
  locale,
  overrideTheme,
  nested,
  proseText,
  marginTopAndBottom,
  appData,
  ignoreFocalPoint,
  productPage,
  region,
  videoProgress,
  setVideoProgress,
  isNested,
  tab,
  siteAppData,
  homepageSlug,
  currentSpaceId,
  products,
  siteIdentifier,
  isPageSection,
}) => {
  switch (content?.__typename) {
    case "ContentfulHeroLead":
      return (
        <Suspense key={content?.contentfulId}>
          <FormatAndRenderHeroLead key={`hero-lead-${i}`} data={content} />
        </Suspense>
      )
    case "ContentfulHeroLeadSearch":
      return (
        <Suspense key={content?.contentfulId}>
          <FormatAndRenderHeroLeadSearch
            key={`hero-lead-search-${i}`}
            data={content}
          />
        </Suspense>
      )
    case "ContentfulHeroSimple":
      return (
        <Suspense key={content?.contentfulId}>
          <FormatAndRenderSimpleHero
            key={`simple-hero-${i}`}
            data={content}
            locale={locale}
            currentSpaceId={currentSpaceId}
            siteIdentifier={siteIdentifier}
            homepageSlug={homepageSlug}
          />
        </Suspense>
      )
    case "ContentfulLayoutStatistics":
      return (
        <Suspense key={content?.contentfulId}>
          <FormatAndRenderLayoutStatistics
            data={content}
            key={`statistics-${i}`}
            locale={locale}
            currentSpaceId={currentSpaceId}
            siteIdentifier={siteIdentifier}
            homepageSlug={homepageSlug}
          />
        </Suspense>
      )
    case "ContentfulLayoutLogoGrid":
      return (
        <Suspense key={content?.contentfulId}>
          <FormatAndRenderLayoutLogoGrid
            data={content}
            key={`logo-grid-${i}`}
            locale={locale}
            currentSpaceId={currentSpaceId}
            siteIdentifier={siteIdentifier}
            homepageSlug={homepageSlug}
          />
        </Suspense>
      )
    case "ContentfulLayoutInformationList":
      return (
        <Suspense key={content?.contentfulId}>
          <FormatAndRenderLayoutFaq
            data={content}
            key={`faq-${i}`}
            locale={locale}
            currentSpaceId={currentSpaceId}
            siteIdentifier={siteIdentifier}
            homepageSlug={homepageSlug}
          />
        </Suspense>
      )
    case "ContentfulLayoutTestimonials":
      return (
        <Suspense key={content?.contentfulId}>
          <FormatAndRenderLayoutTestimonials
            data={content}
            key={`testimonials-${i}`}
            locale={locale}
            currentSpaceId={currentSpaceId}
            siteIdentifier={siteIdentifier}
            homepageSlug={homepageSlug}
          />
        </Suspense>
      )
    case "ContentfulLayoutInlineMedia":
      return (
        <Suspense key={content?.contentfulId}>
          <FormatAndRenderLayoutInlineMedia
            data={content}
            key={`inline-media-${i}`}
            locale={locale}
            currentSpaceId={currentSpaceId}
            siteIdentifier={siteIdentifier}
            homepageSlug={homepageSlug}
          />
        </Suspense>
      )
    case "ContentfulLayoutItemizedList":
      return (
        <Suspense key={content?.contentfulId}>
          <FormatAndRenderLayoutItemizedList
            data={content}
            key={`itemized-list-${i}`}
            locale={locale}
            currentSpaceId={currentSpaceId}
            siteIdentifier={siteIdentifier}
            homepageSlug={homepageSlug}
          />
        </Suspense>
      )
    case "ContentfulLayoutRelatedSubjects":
      return (
        <Suspense key={content?.contentfulId}>
          <FormatAndRenderLayoutRelatedSubjects
            data={content}
            key={`related-subjects-${i}`}
            locale={locale}
            currentSpaceId={currentSpaceId}
            siteIdentifier={siteIdentifier}
            homepageSlug={homepageSlug}
          />
        </Suspense>
      )
    case "ContentfulLayoutCards":
      return (
        <Suspense key={content?.contentfulId}>
          <FormatAndRenderLayoutCards
            key={`cards-${i}`}
            cardType={"bound"}
            data={content}
          />
        </Suspense>
      )
    case "ContentfulLayoutCaseStudies":
      return (
        <Suspense key={content?.contentfulId}>
          <FormatAndRenderLayoutCards
            key={`cards-${i}`}
            cardType={"categorized"}
            data={content}
          />
        </Suspense>
      )
    case "ContentfulLayoutPricingCards":
      return (
        <Suspense key={content?.contentfulId}>
          <FormatAndRenderLayoutPricingCards
            index={content?.contentfulId}
            data={content}
            locale={locale}
            currentSpaceId={currentSpaceId}
            siteIdentifier={siteIdentifier}
            homepageSlug={homepageSlug}
          />
        </Suspense>
      )

    case "ContentfulLayoutGeneral":
      return (
        <Suspense key={content?.contentfulId}>
          <FormatAndRenderLayoutGeneral
            key={`layout-general-${i}`}
            data={content}
            i={i}
            location={location}
            locale={locale}
            appData={appData}
            productPage={productPage ? true : false}
            region={region}
            videoProgress={videoProgress}
            setVideoProgress={setVideoProgress}
            tab={tab ? true : false}
            currentSpaceId={currentSpaceId}
            products={products}
            siteIdentifier={siteIdentifier}
          />
        </Suspense>
      )
    case "ContentfulLayoutProductRecommendations":
      return (
        <Suspense key={content?.contentfulId}>
          <FormatAndRenderLayoutProductRecommendations
            data={content}
            locale={locale}
            currentSpaceId={currentSpaceId}
            siteIdentifier={siteIdentifier}
            homepageSlug={homepageSlug}
          />
        </Suspense>
      )
    case "ContentfulLayoutWorkflow":
      return (
        <Suspense key={content?.contentfulId}>
          <FormatAndRenderLayoutWorkflow
            key={`layout-workflow-${i}`}
            data={content}
            i={i}
            location={location}
            locale={locale}
            appData={appData}
            region={region}
            currentSpaceId={currentSpaceId}
            products={products}
            siteIdentifier={siteIdentifier}
          />
        </Suspense>
      )
    case "ContentfulLayoutFeatureCarousel":
      return (
        <Suspense key={content?.contentfulId}>
          <FormatAndRenderLayoutFeatureCarousel
            key={`feature-carousel-${i}`}
            data={content}
            locale={locale}
            currentSpaceId={currentSpaceId}
            siteIdentifier={siteIdentifier}
            homepageSlug={homepageSlug}
          />
        </Suspense>
      )
    case "ContentfulLayoutSubjectSpotlight":
      return (
        <Suspense key={content?.contentfulId}>
          <FormatAndRenderLayoutSubjectSpotlight
            key={`subject-spotlight-${i}`}
            data={content}
            locale={locale}
            currentSpaceId={currentSpaceId}
            siteIdentifier={siteIdentifier}
            homepageSlug={homepageSlug}
          />
        </Suspense>
      )
    case "ContentfulLayoutBlogDigest":
      return (
        <Suspense key={content?.contentfulId}>
          <FormatAndRenderLayoutBlogDigest
            key={`blog-digest-${i}`}
            data={content}
            locale={locale}
            currentSpaceId={currentSpaceId}
            siteIdentifier={siteIdentifier}
            homepageSlug={homepageSlug}
          />
        </Suspense>
      )
    case "ContentfulLayoutForm":
      return (
        <Suspense key={content?.contentfulId}>
          <FormatAndRenderLayoutForm
            key={`feature-carousel-${i}`}
            data={content}
            locale={locale}
            currentSpaceId={currentSpaceId}
            siteIdentifier={siteIdentifier}
            homepageSlug={homepageSlug}
          />
        </Suspense>
      )

    case "ContentfulLayoutResourceGrid":
      return (
        <Suspense key={content?.contentfulId}>
          <FormatAndRenderLayoutResourceGrid
            key={`resource-grid-${i}`}
            data={content}
            locale={locale}
            currentSpaceId={currentSpaceId}
            siteIdentifier={siteIdentifier}
            homepageSlug={homepageSlug}
          />
        </Suspense>
      )
    case "ContentfulLayoutComparisonTable":
      return (
        <Suspense key={content?.contentfulId}>
          <FormatAndRenderLayoutComparisonTable
            key={`comparison-table-${i}`}
            data={content}
            locale={locale}
            currentSpaceId={currentSpaceId}
            siteIdentifier={siteIdentifier}
            homepageSlug={homepageSlug}
          />
        </Suspense>
      )
    default:
      return null
  }
}
